import {useCurrentMaterialStorages, isStorageWithMaterialGuard} from '@domain/material-storage'
import {MaterialType} from '@domain/materials'
import {useMediaQueryBreakpoints} from '@hconnect/common/hooks'
import {roundTo15MinIntervalStart} from '@hconnect/common/utils'
import {dataTestId} from '@hconnect/uikit'
import {useUpmPlantTimezone, useUpmPlantContext} from '@hconnect/upm'
import {Box} from '@mui/material'
import {useCurrentTimeRounded} from '@widgets/stock-card'
import {useMemo} from 'react'

import {SiloCard} from '../components'
import {useStockReachQuery, useSearchParams} from '../hooks'
import {getStorageToMaterialAmount} from '../selectors'
import {fitElementsToGridBasedOnColSpan} from '../utils'

const MAX_REACH_IN_DAYS = 30

type Props = {
  stockMaterialsOrdersUrl: string
}

export const SilosContainer = ({stockMaterialsOrdersUrl}: Props) => {
  const [obtainedParams] = useSearchParams('materialType')
  const {plantId: plantCode} = useUpmPlantContext()
  const obtainedMaterialType = obtainedParams.materialType
  if (obtainedMaterialType === null) {
    throw new Error('BUG: materialType search param should be set before rendering this component')
  }
  const selectedMaterialType = obtainedMaterialType as MaterialType

  const {sm, md, lg} = useMediaQueryBreakpoints()
  const timezoneId = useUpmPlantTimezone()
  const {data: storagesWithMaterial} = useCurrentMaterialStorages({
    plantId: plantCode,
    options: {select: (storages) => storages.filter(isStorageWithMaterialGuard), throwOnError: true}
  })

  const endTime = useCurrentTimeRounded({timezoneId, roundingFn: roundTo15MinIntervalStart})
    .clone()
    .add(MAX_REACH_IN_DAYS, 'day')
    .toISOString()

  // TODO: Improve error handling after suite integration
  if (!storagesWithMaterial) {
    throw new Error('Bug: storages with material should be loaded to render this component')
  }

  const columns = sm ? 1 : md ? 2 : lg ? 3 : 4

  const materialIds = useMemo(() => {
    return [...new Set(storagesWithMaterial.map((storage) => storage.materialId))]
  }, [storagesWithMaterial])

  const {data: stockReach} = useStockReachQuery({
    materialIds,
    plantCode,
    timezoneId,
    endTime
  })

  const allMaterialTypesInfo = useMemo(
    () =>
      getStorageToMaterialAmount({
        storagesWithMaterial,
        stockReach
      }),
    [storagesWithMaterial, stockReach]
  )

  const materialsForTypeInfo = allMaterialTypesInfo.filter(
    (materialInfo) => materialInfo.materialType === selectedMaterialType
  )

  const materialInfoWithColSpan = materialsForTypeInfo.map((material) => ({
    colSpan: material.storages.length > 5 && columns > 1 ? 2 : 1,
    data: material
  }))
  const sortedMaterialInfo = fitElementsToGridBasedOnColSpan(columns, materialInfoWithColSpan)

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: `repeat(${columns}, 1fr)`
      }}
      {...dataTestId('page_stockDevelopment')}
    >
      {sortedMaterialInfo.map(({data: material, colSpan}) => (
        <SiloCard
          stockMaterialsOrdersUrl={stockMaterialsOrdersUrl}
          key={material.materialId}
          materialInfo={material}
          timezoneId={timezoneId}
          colSpan={colSpan}
        />
      ))}
    </Box>
  )
}
